import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// ── Настройки AI-анализа (общий кэш на всё приложение: один запрос на все значки) ──────────
let settingsCache = null;       // { at, data }
let settingsPromise = null;
const settingsListeners = new Set();

export function loadCallAiSettings(force = false) {
  if (!force && settingsCache && Date.now() - settingsCache.at < 60000) return Promise.resolve(settingsCache.data);
  if (!settingsPromise) {
    settingsPromise = axios.get(`${API}/call-analysis/settings`)
      .then(r => { settingsCache = { at: Date.now(), data: r.data }; return r.data; })
      .catch(() => { settingsCache = { at: Date.now(), data: null }; return null; })
      .finally(() => { settingsPromise = null; settingsListeners.forEach(fn => fn()); });
  }
  return settingsPromise;
}
export function resetCallAiSettings() { settingsCache = null; }

export function useCallAiSettings() {
  const [data, setData] = useState(settingsCache?.data ?? undefined);   // undefined — ещё грузится
  useEffect(() => {
    let alive = true;
    const sync = () => { if (alive && settingsCache) setData(settingsCache.data); };
    settingsListeners.add(sync);
    loadCallAiSettings().then(d => { if (alive) setData(d); });
    return () => { alive = false; settingsListeners.delete(sync); };
  }, []);
  const reload = useCallback(() => loadCallAiSettings(true), []);
  return { data, settings: data?.settings, engine: data?.engine, canEdit: !!data?.canEdit, loading: data === undefined, reload };
}

// ── Краткий статус по звонку (для значков). Запросы склеиваются в один /brief ────────────────
const briefCache = new Map();   // callId → { at, item|null }
const waiters = new Map();      // callId → Set<fn>
let queue = new Set();
let timer = null;

function flush() {
  const ids = [...queue]; queue = new Set(); timer = null;
  axios.get(`${API}/call-analysis/brief`, { params: { ids: ids.join(',') } })
    .then(r => ids.forEach(id => briefCache.set(id, { at: Date.now(), item: r.data.items?.[id] || null })))
    .catch(() => ids.forEach(id => briefCache.set(id, { at: Date.now(), item: null })))
    .finally(() => ids.forEach(id => (waiters.get(id) || []).forEach(fn => fn())));
}

export function invalidateBrief(callId) {
  briefCache.delete(String(callId));
  queue.add(String(callId));
  if (!timer) timer = setTimeout(flush, 30);
}

export function useCallBrief(callId, enabled = true) {
  const id = String(callId || '');
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled || !id) return undefined;
    const fn = () => force(x => x + 1);
    if (!waiters.has(id)) waiters.set(id, new Set());
    waiters.get(id).add(fn);
    const hit = briefCache.get(id);
    if (!hit || Date.now() - hit.at > 30000) { queue.add(id); if (!timer) timer = setTimeout(flush, 30); }
    return () => { waiters.get(id)?.delete(fn); };
  }, [id, enabled]);
  return briefCache.get(id)?.item;       // undefined — грузится, null — анализа нет
}

// ── Оформление ──────────────────────────────────────────────────────────────────────────────
export const IN_PROGRESS = ['queued', 'downloading', 'downloaded', 'transcribing', 'transcribed', 'analyzing'];

export function scoreTone(score) {
  if (score == null) return { text: 'text-ink-tertiary', bg: 'bg-surface-100', bar: 'bg-surface-300', ring: '#cbd5e1' };
  if (score >= 80) return { text: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'bg-emerald-500', ring: '#10b981' };
  if (score >= 60) return { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-amber-500', ring: '#f59e0b' };
  return { text: 'text-red-600', bg: 'bg-red-50', bar: 'bg-red-500', ring: '#ef4444' };
}

export const DEFAULT_ITEM_KEYS = ['greeting', 'needs', 'presentation', 'objections', 'next_step', 'politeness', 'closing'];

// Название пункта чек-листа: стандартные переводим сами, свои — как заданы в настройках
export function itemTitle(item, t, defaults) {
  const key = item.key;
  if (DEFAULT_ITEM_KEYS.includes(key)) {
    const def = (defaults || []).find(d => d.key === key);
    if (!item.title || !def || item.title === def.title) return t('callAI.item.' + key);
  }
  return item.title || key;
}

export const fmtClock = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};
